<template>
  <div class="deck-builder">
    <div v-if="isLoading" class="loading-overlay">
      <p>カードデータベースを読み込んでいます...</p>
    </div>

    <div class="builder-container" v-else @dragover="handleDragScroll">
      <div class="builder-inner">
        <div class="search-panel">
          <h2>カード検索</h2>
          <div class="filter-group">
            <label>カード名</label>
            <input type="text" v-model="searchText" placeholder="カード名で検索..." />
          </div>

          <div class="filter-group">
            <label>タイプ</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterTypes[CARD_TYPE_MEMBER]"> メンバー
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterTypes[CARD_TYPE_LIVE]"> ライブ
              </label>
            </div>
          </div>

          <div class="accordion">
            <div class="accordion-header" @click="isAccordionOpen = !isAccordionOpen">
              <span>詳細検索</span>
              <span class="arrow" :class="{ 'open': isAccordionOpen }"></span>
            </div>
            <div class="accordion-content" v-show="isAccordionOpen">
              <div class="filter-group">
                <label>収録商品</label>
                <select v-model="filterProduct" class="series-select">
                  <option value="すべて">すべて</option>
                  <option v-for="product in allProducts" :key="product" :value="product">{{ product }}</option>
                </select>
              </div>

              <div class="filter-group">
                <label>シリーズ</label>
                <select v-model="filterSeries" class="series-select">
                  <option value="すべて">すべて</option>
                  <option v-for="series in allSeries" :key="series" :value="series">{{ series }}</option>
                </select>
              </div>

              <div class="filter-group">
                <label>ユニット</label>
                <select v-model="filterUnit" class="series-select">
                  <option value="すべて">すべて</option>
                  <option v-for="unit in allUnits" :key="unit" :value="unit">{{ unit }}</option>
                </select>
              </div>

              <div class="filter-group">
                <label>コスト</label>
                <div class="checkbox-group cost-checkbox-group">
                  <label v-for="cost in Object.keys(filterCosts).map(Number).sort((a, b) => a - b)" :key="cost"
                    class="checkbox-label">
                    <input type="checkbox" v-model="filterCosts[cost]"> {{ cost }}
                  </label>
                </div>
                <div class="cost-actions">
                  <button @click="toggleAllCosts(true)">すべて選択</button>
                  <button @click="toggleAllCosts(false)">すべて解除</button>
                </div>
              </div>
            </div>
          </div>

          <div class="usage-guide">
            <h4>デッキログから読み込む手順</h4>
            <ul>
              <li><strong>1.</strong><br><a href="https://ws4696.xyz/decklog/" target="_blank"
                  rel="noopener noreferrer">デッキログ解析くん(外部サイト)</a>にアクセスし、デッキコードを入力</li>
              <li><strong>2.</strong><br>「解析」をクリックし、デッキリストをコピー(詳細設定の変更は不要)</li>
              <li><strong>3.</strong><br>このサイトに戻り、「デッキをテキストで編集」を開き、コピーしたテキストを貼り付ける</li>
              <li><strong>謝辞</strong><br>この場を借りてデッキログ解析くんの作者様にお礼を申し上げます。</li>
            </ul>
          </div>
        </div>

        <div class="card-list-panel">
          <div v-if="filteredCards.length === 0" class="initial-message">
            <p>条件に一致するカードがありません。<br>検索ワードやフィルターを確認してください。</p>
          </div>

          <div class="card-grid">
            <div v-for="card in filteredCards" :key="card.card_no" class="card-thumbnail" @click="addCardToDeck(card)">
              <img :src="card.img" :alt="card.name" loading="lazy" />
            </div>
          </div>
        </div>

        <div class="deck-panel">
          <div class="deck-panel-header">
            <div class="deck-panel-actions">
              <button class="start-game-button" @click="startSoloPlay" :disabled="deckTotalCount === 0">
                このデッキで一人回しを始める
              </button>
              <div class="deck-actions">
                <button class="action-button import-button" @click="triggerFileInput">インポート (.txt)</button>
                <input type="file" ref="fileInput" @change="handleFileImport" accept=".txt" style="display: none;" />
                <button class="action-button export-button" @click="exportDeck">エクスポート (.txt)</button>
                <button class="action-button edit-text-button" @click="openTextEditor">デッキをテキストで編集</button>
                <button class="action-button clear-button" @click="clearDeck">デッキをクリア</button>
              </div>
            </div>
            <h2>デッキ ({{ deckTotalCount }} / {{ DECK_SIZE_LIMIT }})</h2>
            <div class="deck-count-details">
              <span :class="{ 'is-over-limit': memberCardCount > MEMBER_CARD_LIMIT }">
                メンバー: {{ memberCardCount }} / {{ MEMBER_CARD_LIMIT }}
              </span>
              <span :class="{ 'is-over-limit': liveCardCount > LIVE_CARD_LIMIT }">
                ライブ: {{ liveCardCount }} / {{ LIVE_CARD_LIMIT }}
              </span>
            </div>
          </div>
          <ul class="deck-list">
            <li v-if="deckTotalCount === 0">
              <p class="empty-deck-message">左のリストからカードをクリックしてデッキに追加します。</p>
            </li>
            <li v-for="entry in deckListWithDetails" :key="entry.card.card_no">
              <div class="deck-list-card-info">
                <img :src="entry.card.img" class="deck-list-thumbnail" :alt="entry.card.name" />
                <span :title="entry.card.name">{{ entry.card.name }}</span>
              </div>
              <div class="deck-card-controls">
                <button @click="removeCardFromDeck(entry.card.card_no)">-</button>
                <span>x {{ entry.count }}</span>
                <button @click="addCardToDeck(entry.card)">+</button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="isEditingAsText" class="text-editor-overlay">
      <div class="text-editor-content">
        <h3>デッキをテキストで編集</h3>
        <p>形式: 「枚数 x カードID カード名」で1行に1種類ずつ入力してください。</p>
        <textarea v-model="deckAsText" class="deck-textarea" placeholder="例: 4 x PL!N-bp1-002-P 中須かすみ"></textarea>
        <div class="text-editor-actions">
          <button class="action-button apply-button" @click="applyTextDeck">適用して閉じる</button>
          <button class="action-button cancel-button" @click="isEditingAsText = false">キャンセル</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DECK_SIZE_LIMIT,
  MAX_COPIES_PER_CARD,
  MEMBER_CARD_LIMIT,
  LIVE_CARD_LIMIT,
  LOCAL_DATA_URL,
  REMOTE_DATA_URL,
  CARD_TYPE_MEMBER,
  CARD_TYPE_LIVE,
  CARD_TYPE_ENERGY
} from '../constants';

export default {
  name: 'DeckBuilder',
  data() {
    return {
      allCards: [],
      deck: {},
      deckAsText: '',
      searchText: '',
      isLoading: true,
      filterTypes: {
        [CARD_TYPE_MEMBER]: true,
        [CARD_TYPE_LIVE]: true,
      },
      isAccordionOpen: false,
      allProducts: [],
      filterProduct: 'すべて',
      allSeries: [],
      filterSeries: 'すべて',
      allUnits: [],
      filterUnit: 'すべて',
      filterCosts: {},
      saveTimeout: null,
      isEditingAsText: false,
      // Make constants available in template
      DECK_SIZE_LIMIT,
      MEMBER_CARD_LIMIT,
      LIVE_CARD_LIMIT,
      CARD_TYPE_MEMBER,
      CARD_TYPE_LIVE
    };
  },
  computed: {
    deckTotalCount() {
      return Object.values(this.deck).reduce((sum, count) => sum + count, 0);
    },

    filteredCards() {
      // 選択されているタイプをリストアップ
      const selectedTypes = Object.keys(this.filterTypes).filter(type => this.filterTypes[type]);

      // ここからコストフィルターの選択値を取得
      const selectedCosts = Object.keys(this.filterCosts)
        .filter(cost => this.filterCosts[cost])
        .map(Number);

      if (selectedTypes.length === 0) {
        return [];
      }

      // 「コストが全選択されているか」を判定するフラグ
      const areAllCostsSelected = Object.keys(this.filterCosts).length === selectedCosts.length;

      // 検索テキストが空の場合は何も表示しない
      if (!this.searchText.trim() &&
        selectedTypes.includes(CARD_TYPE_MEMBER) &&
        this.filterProduct === 'すべて' &&
        this.filterSeries === 'すべて' &&
        this.filterUnit === 'すべて' &&
        areAllCostsSelected) {
        return [];
      }

      return this.allCards.filter(card => {
        // エネルギーカードは常に除外
        const isNotEnergy = card.type !== CARD_TYPE_ENERGY;
        // カード名での絞り込み
        const nameMatch = (card.name || '').toLowerCase().includes(this.searchText.toLowerCase());
        // タイプでの絞り込み
        const typeMatch = selectedTypes.includes(card.type);
        // 収録商品絞り込み: カードのproductプロパティが選択中の商品と一致するかチェック
        const productMatch = this.filterProduct === 'すべて' || card.product === this.filterProduct;
        // シリーズ絞り込み: カードのseriesプロパティに選択中のシリーズが含まれるかチェック
        const seriesMatch = this.filterSeries === 'すべて' ||
          (card.series && card.series.split('\n').map(s => s.trim()).includes(this.filterSeries));
        // ユニット絞り込み: カードのunitプロパティが選択中のユニットと一致するかチェック
        const unitMatch = this.filterUnit === 'すべて' || card.unit === this.filterUnit;
        // コスト絞り込み: カードが「ライブ」タイプの場合はコストを無視。それ以外は選択中のコストに含まれるかチェック
        const costMatch = card.type === CARD_TYPE_LIVE || selectedCosts.includes(card.cost);

        return isNotEnergy && nameMatch && typeMatch && productMatch && seriesMatch && unitMatch && costMatch;
      });
    },

    deckListWithDetails() {
      return Object.entries(this.deck)
        .map(([cardId, count]) => {
          return {
            card: this.allCards.find(c => c.card_no === cardId),
            count: count
          };
        })
        .filter(entry => entry.card)
        // カード種別やカード番号でソートするなど、必要に応じて変更
        .sort((a, b) => {
          if (a.card.type !== b.card.type) {
            return a.card.type > b.card.type ? 1 : -1;
          }
          return a.card.card_no > b.card.card_no ? 1 : -1;
        });
    },

    memberCardCount() {
      return this.deckListWithDetails.reduce((sum, entry) => {
        if (entry.card.type === CARD_TYPE_MEMBER) {
          return sum + entry.count;
        }
        return sum;
      }, 0);
    },

    liveCardCount() {
      return this.deckListWithDetails.reduce((sum, entry) => {
        if (entry.card.type === CARD_TYPE_LIVE) {
          return sum + entry.count;
        }
        return sum;
      }, 0);
    },
  },
  watch: {
    deck: {
      deep: true,
      handler(newDeck) {
        // 既存のタイマーがあればクリア
        clearTimeout(this.saveTimeout);

        // 500ミリ秒（0.5秒）後に保存処理を実行する新しいタイマーをセット
        this.saveTimeout = setTimeout(() => {
          localStorage.setItem('llocg_deck', JSON.stringify(newDeck));
          console.log('Deck saved to localStorage.'); // デバッグ用ログ
        }, 500);
      }
    }
  },
  beforeUnmount() {
    // コンポーネントが破棄される直前に、保留中の保存タイマーをクリア
    clearTimeout(this.saveTimeout);

    // まだ保存されていない最新のデッキ状態があれば、即座に保存する
    // これにより、ページを閉じる際に変更が失われるのを防ぎます
    const savedDeck = localStorage.getItem('llocg_deck');
    if (JSON.stringify(this.deck) !== savedDeck) {
      console.log('Saving final deck state before unmount.'); // デバッグ用ログ
      localStorage.setItem('llocg_deck', JSON.stringify(this.deck));
    }
  },
  async created() {
    this.loadDeckFromStorage();
    await this.loadCardDatabase();
  },
  methods: {
    loadDeckFromStorage() {
      const savedDeck = localStorage.getItem('llocg_deck');
      if (savedDeck) {
        try {
          this.deck = JSON.parse(savedDeck);
        } catch (e) {
          console.error("ローカルストレージのデッキデータの解析に失敗しました。", e);
          localStorage.removeItem('llocg_deck');
        }
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    /**
     * テキスト形式を解釈してデッキをインポートします。
     * 形式: "枚数 x カードID カード名"
     */
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;

      // ファイルサイズをチェック (例: 1MBに制限)
      const MAX_SIZE_MB = 1;
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        alert(`ファイルサイズが大きすぎます。${MAX_SIZE_MB}MB以下のファイルを選択してください。`);
        event.target.value = ''; // inputの値をリセット
        return;
      }

      if (this.deckTotalCount > 0 && !confirm('現在のデッキ内容はクリアされます。ファイルからデッキを読み込みますか？')) {
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split(/\r?\n/);

          // 行数をチェック (例: 10,000行に制限)
          const MAX_LINES = 10000;
          if (lines.length > MAX_LINES) {
            alert(`行数が多すぎます。${MAX_LINES}行以下のファイルにしてください。`);
            return; // finallyブロックは実行される
          }

          const newDeck = {};
          let errorLines = [];

          // "4 x PL!N-bp1-002-P 中須かすみ" のような形式をパースする正規表現
          // ^(\d+): 行頭の数字（枚数）
          // \s+x\s+: " x " という区切り文字
          // ([^\s]+): スペースを含まない文字列（カードID）
          // \s+(.+)$: 残りのすべての文字列（カード名）
          const lineRegex = /^(\d+)\s+x\s+([^\s]+)\s+(.+)$/;

          lines.forEach((line, index) => {
            if (!line.trim()) return; // 空行は無視

            const match = line.trim().match(lineRegex);

            if (!match) { // 正規表現にマッチしない行はエラー
              errorLines.push(index + 1);
              return;
            }

            // match[1]が枚数, match[2]がカードID, match[3]がカード名
            const count = parseInt(match[1], 10);
            const cardId = match[2];
            const nameFromFile = match[3].trim(); // ファイルから読み取ったカード名

            // --- ここから検証ロジック ---
            const correctCardData = this.allCards.find(c => c.card_no === cardId);
            let isValid = true;

            if (!correctCardData) {
              // 1. カードIDがデータベースに存在しない場合
              isValid = false;
            } else if (correctCardData.name !== nameFromFile) {
              // 2. カードIDとファイル上のカード名が一致しない場合
              isValid = false;
            } else if (isNaN(count) || count <= 0 || count > MAX_COPIES_PER_CARD) {
              // 3. 枚数が無効な場合
              isValid = false;
            }

            if (isValid) {
              newDeck[cardId] = (newDeck[cardId] || 0) + count;
              if (newDeck[cardId] > MAX_COPIES_PER_CARD) newDeck[cardId] = MAX_COPIES_PER_CARD;
            } else {
              errorLines.push(index + 1);
            }
          });

          if (errorLines.length > 0) {
            alert(`エラー: ${errorLines.join(', ')}行目で問題が見つかりました。\n\n・形式が正しくない\n・カードIDが存在しない\n・カードIDとカード名が一致しない\n・枚数が正しくない\n\nのいずれかの可能性があります。`);
          } else {
            this.deck = newDeck;
            alert('デッキをインポートしました。');
          }

        } catch (err) {
          alert('ファイルの読み込みに失敗しました。');
          console.error(err);
        } finally {
          event.target.value = ''; // 同じファイルを連続で選択できるようにリセット
        }
      };
      // 日本語のカード名が含まれる可能性があるため、UTF-8で読み込む
      reader.readAsText(file, 'UTF-8');
    },

    /**
     * デッキをテキスト形式でエクスポートします。
     * 形式: "枚数 x カードID カード名"
     */
    exportDeck() {
      if (this.deckTotalCount === 0) {
        alert('デッキにカードがありません。');
        return;
      }

      // deckListWithDetailsにはカード名も含まれている
      const content = this.deckListWithDetails
        .map(entry => `${entry.count} x ${entry.card.card_no} ${entry.card.name}`)
        .join('\n');

      // 日本語を含むため、UTF-8でBlobを生成
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deck.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    /**
     * テキスト編集モーダルを開きます。
     */
    openTextEditor() {
      // 現在のデッキをテキスト形式に変換して、textareaに表示
      this.deckAsText = this.deckListWithDetails
        .map(entry => `${entry.count} x ${entry.card.card_no} ${entry.card.name}`)
        .join('\n');

      // モーダルを表示
      this.isEditingAsText = true;
    },

    /**
     * テキストエリアの内容を解析し、デッキに適用します。
     */
    applyTextDeck() {
      const lines = this.deckAsText.split(/\r?\n/);
      const newDeck = {};
      let errorLines = [];
      const lineRegex = /^(\d+)\s+x\s+([^\s]+)\s+(.+)$/;

      lines.forEach((line, index) => {
        if (!line.trim()) return;

        const match = line.trim().match(lineRegex);
        if (!match) {
          errorLines.push(index + 1);
          return;
        }

        const count = parseInt(match[1], 10);
        const cardId = match[2];
        const nameFromFile = match[3].trim();

        const correctCardData = this.allCards.find(c => c.card_no === cardId);
        let isValid = true;

        if (!correctCardData || correctCardData.name !== nameFromFile || isNaN(count) || count <= 0 || count > MAX_COPIES_PER_CARD) {
          isValid = false;
        }

        if (isValid) {
          newDeck[cardId] = (newDeck[cardId] || 0) + count;
          if (newDeck[cardId] > MAX_COPIES_PER_CARD) newDeck[cardId] = MAX_COPIES_PER_CARD;
        } else {
          errorLines.push(index + 1);
        }
      });

      if (errorLines.length > 0) {
        alert(`エラー: ${errorLines.join(', ')}行目で問題が見つかりました。\n\n形式、カードID、カード名、または枚数を確認してください。`);
      } else {
        // パース成功
        this.deck = newDeck;
        alert('デッキを更新しました。');
        this.isEditingAsText = false; // モーダルを閉じる
      }
    },

    clearDeck() {
      if (this.deckTotalCount > 0 && confirm('本当に現在のデッキをクリアしますか？')) {
        this.deck = {};
      }
    },

    async loadCardDatabase() {
      try {
        this.isLoading = true;
        const localUrl = LOCAL_DATA_URL;
        // 取得したい外部JSONのURL
        const remoteUrl = REMOTE_DATA_URL;

        // Promise.allで2つのファイルを並行して取得
        const [localResponse, remoteResponse] = await Promise.all([
          fetch(localUrl),
          fetch(remoteUrl)
        ]);

        // 両方のレスポンスが正常かチェック
        if (!localResponse.ok) throw new Error('ローカルのカードDBの読み込みに失敗しました。');
        if (!remoteResponse.ok) throw new Error('外部のカードDBの読み込みに失敗しました。');

        // それぞれをJSONとして解析
        const localData = await localResponse.json();
        const remoteData = await remoteResponse.json();

        // 2つのJSONオブジェクトを1つにマージ
        const combinedData = { ...remoteData, ...localData };

        // キーがアンダースコアで始まらないデータのみを抽出する
        this.allCards = Object.entries(combinedData)
          .filter(([key]) => !key.startsWith('_'))
          .map(([, value]) => value);

        const cardsForFilter = this.allCards.filter(card => card.type !== CARD_TYPE_ENERGY);

        const SERIES_ORDER = [
          'ラブライブ！',
          'ラブライブ！サンシャイン!!',
          'ラブライブ！虹ヶ咲学園スクールアイドル同好会',
          'ラブライブ！スーパースター!!',
          '蓮ノ空女学院スクールアイドルクラブ',
        ];

        const UNIT_ORDER = [
          'Printemps', 'lilywhite', 'BiBi',
          'CYaRon！', 'GuiltyKiss', 'AZALEA',
          'A・ZU・NA', 'QU4RTZ', 'DiverDiva', 'R3BIRTH',
          'CatChu!', 'KALEIDOSCORE', '5yncri5e!',
          'スリーズブーケ', 'DOLLCHESTRA', 'みらくらぱーく!',
        ];

        /**
         * @param {string} key - 'series', 'product'など、カードオブジェクトのキー名
         * @param {Array<string>} [orderArray=[]] - 並び順を指定する配列（任意）
         * @returns {Array<string>} - 並べ替えたユニークな値の配列
         */
        const createCustomSortedList = (sourceCards, key, orderArray = []) => {
          const valueSet = new Set();
          sourceCards.forEach(card => {
            if (card[key]) {
              card[key].split('\n').forEach(s => valueSet.add(s.trim()));
            }
          });

          const uniqueValues = Array.from(valueSet).filter(Boolean);

          uniqueValues.sort((a, b) => {
            const indexA = orderArray.indexOf(a);
            const indexB = orderArray.indexOf(b);

            // 両方の項目が順序配列に存在する場合、その順番に従う
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // aだけが順序配列に存在する場合、aを前にする
            if (indexA !== -1) {
              return -1;
            }
            // bだけが順序配列に存在する場合、bを前にする
            if (indexB !== -1) {
              return 1;
            }
            // どちらも順序配列にない場合、辞書順でソートする
            return a.localeCompare(b);
          });

          return uniqueValues;
        };

        this.allProducts = createCustomSortedList(cardsForFilter, 'product');
        this.allSeries = createCustomSortedList(cardsForFilter, 'series', SERIES_ORDER);
        this.allUnits = createCustomSortedList(cardsForFilter, 'unit', UNIT_ORDER);

        // ユニークなコストリストを作成（メンバーカードのみ対象）
        const costSet = new Set(
          cardsForFilter
            .filter(card => card.type === CARD_TYPE_MEMBER && typeof card.cost === 'number')
            .map(card => card.cost)
        );

        // filterCostsオブジェクトを動的に生成
        const newFilterCosts = {};
        Array.from(costSet).sort((a, b) => a - b).forEach(cost => {
          newFilterCosts[cost] = true; // 初期状態はすべてチェック済みにする
        });
        this.filterCosts = newFilterCosts;
      } catch (error) {
        console.error(error);
        alert(error.message);
      } finally {
        this.isLoading = false;
      }
    },

    toggleAllCosts(checked) {
      for (const cost in this.filterCosts) {
        this.filterCosts[cost] = checked;
      }
    },

    addCardToDeck(card) {
      const cardId = card.card_no;
      const currentCount = this.deck[cardId] || 0;

      if (this.deckTotalCount >= DECK_SIZE_LIMIT) {
        alert(`デッキの枚数上限は${DECK_SIZE_LIMIT}枚です。`);
        return;
      }

      if (currentCount >= MAX_COPIES_PER_CARD) {
        alert(`同じカードは${MAX_COPIES_PER_CARD}枚までしか入れられません。`);
        return;
      }

      if (card.type === CARD_TYPE_MEMBER && this.memberCardCount >= MEMBER_CARD_LIMIT) {
        alert(`メンバーカードの上限は${MEMBER_CARD_LIMIT}枚です。`);
        return;
      }

      if (card.type === CARD_TYPE_LIVE && this.liveCardCount >= LIVE_CARD_LIMIT) {
        alert(`ライブカードの上限は${LIVE_CARD_LIMIT}枚です。`);
        return;
      }

      this.deck = {
        ...this.deck,
        [cardId]: currentCount + 1,
      };
    },

    removeCardFromDeck(cardId) {
      const currentCount = this.deck[cardId];
      if (!currentCount) return;

      if (currentCount > 1) {
        this.deck = {
          ...this.deck,
          [cardId]: currentCount - 1,
        };
      } else {
        const newDeck = { ...this.deck };
        delete newDeck[cardId];
        this.deck = newDeck;
      }
    },

    startSoloPlay() {
      if (this.deckTotalCount === 0) {
        alert('デッキにカードが1枚もありません。');
        return;
      }
      this.$emit('start-game');
    },
  }
};
</script>

<style scoped>
h2 {
  margin-top: 10px;
  margin-bottom: 20px;
  color: var(--text-light, #ecf0f1);
  border-bottom: 2px solid var(--area-border, #7f8c8d);
  padding-bottom: 10px;
  flex-shrink: 0;
}

h4 {
  border-top: 2px solid var(--area-border, #7f8c8d);
  padding-top: 10px;
}

.deck-builder {
  width: 100%;
  height: 100vh;
  color: var(--text-light, #ecf0f1);
  background-color: var(--board-bg, #34495e);
  overflow: hidden;
  display: flex;
  /* height: 100vh を子要素に効かせるため */
  flex-direction: column;
}

.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.5em;
  color: var(--text-light, #ecf0f1);
}

.builder-container {
  flex-grow: 1;
  /* 残りの高さをすべて使う */
  overflow-x: auto;
  /* この要素で横スクロールを可能にする */
  overflow-y: auto;
  /* 垂直方向のスクロールも有効にする */
  padding: 15px;
  /* パディングをこちらに移動 */
  /* display: grid; などは builder-inner に移動 */
}

.builder-inner {
  display: grid;
  grid-template-columns: 280px 1fr 380px;
  gap: 15px;
  min-width: 1000px;
  height: 100%;
  min-height: 650px;
}

.search-panel,
.card-list-panel {
  background-color: var(--header-bg, #2c3e50);
  border: 1px solid var(--area-border, #7f8c8d);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.deck-panel {
  background-color: var(--header-bg, #2c3e50);
  border: 1px solid var(--area-border, #7f8c8d);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  /* 親要素からあふれないように高さを制限 */
  max-height: 100%;
}

.deck-panel-header {
  flex-shrink: 0;
  /* ヘッダーが縮まないように */
}

.deck-panel-header h2 {
  margin-bottom: 10px;
  /* h2と内訳表示の間のマージンを調整 */
}

.deck-count-details {
  font-size: 0.9em;
  color: var(--text-light, #ecf0f1);
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--area-border, #7f8c8d);
  display: flex;
  justify-content: space-around;
  gap: 10px;
}

.deck-count-details span.is-over-limit {
  color: #e74c3c;
  /* 上限を超えた場合に文字を赤くする */
  font-weight: bold;
}

.initial-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #95a5a6;
  text-align: center;
}

.filter-group {
  margin-bottom: 15px;
}

.filter-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.filter-group input[type="text"] {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--area-border, #7f8c8d);
  background-color: var(--board-bg, #34495e);
  color: var(--text-light, #ecf0f1);
  border-radius: 4px;
  box-sizing: border-box;
  /* paddingを含めた幅計算に */
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  /* チェックボックスが多い場合に折り返す */
  gap: 15px;
  margin-top: 5px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.9em;
}

.checkbox-label input {
  margin-right: 5px;
}

.accordion {
  margin-bottom: 20px;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 5px 0;
}

.accordion-header span {
  font-weight: bold;
  font-size: 1.1em;
}

.accordion-header .arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid var(--text-light, #ecf0f1);
  transition: transform 0.3s;
}

.accordion-header .arrow.open {
  transform: rotate(180deg);
}

.accordion-content {
  padding-top: 15px;
}

.series-select {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--area-border, #7f8c8d);
  background-color: var(--board-bg, #34495e);
  color: var(--text-light, #ecf0f1);
  border-radius: 4px;
  box-sizing: border-box;
}

.cost-checkbox-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: 10px 15px;
}

.cost-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.cost-actions button {
  padding: 4px 8px;
  font-size: 0.8em;
  border: 1px solid var(--area-border, #7f8c8d);
  background-color: transparent;
  color: var(--text-light, #ecf0f1);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.cost-actions button:hover {
  background-color: var(--area-border, #7f8c8d);
  color: var(--header-bg, #2c3e50);
}

.usage-guide {
  color: #bdc3c7;
  /* 少し明るめのグレー */
}

.usage-guide h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--text-light, #ecf0f1);
  font-size: 1.1em;
}

.usage-guide ul {
  list-style: none;
  /* 黒丸を削除 */
  padding-left: 0;
  margin: 0;
  font-size: 0.9em;
  line-height: 1.6;
}

.usage-guide li {
  margin-bottom: 12px;
}

.usage-guide li:last-child {
  margin-bottom: 0;
}

.usage-guide strong {
  color: var(--text-light, #ecf0f1);
  font-weight: 600;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 15px;
  padding-right: 10px;
}

.card-thumbnail img {
  width: 100%;
  border-radius: 5px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: block;
}

.card-thumbnail img:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(236, 240, 241, 0.2);
}

.deck-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
  /* 利用可能なスペースを埋めるように拡張 */
  overflow-y: auto;
  /* 内容が多い場合にスクロールバーを表示 */
  min-height: 0;
  /* flexアイテムの縮小に関する問題を回避 */
  border-top: 1px solid var(--area-border, #7f8c8d);
}

.empty-deck-message {
  color: #95a5a6;
  text-align: center;
  margin-top: 20px;
}

.deck-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
  border-bottom: 1px solid var(--area-border, #7f8c8d);
}

.deck-list li:last-child {
  border-bottom: none;
}

.deck-list-card-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.deck-list-thumbnail {
  height: 40px;
  width: auto;
  border-radius: 3px;
  display: block;
}

.deck-list-card-info>span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9em;
}

.deck-card-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.deck-card-controls button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--text-light, #ecf0f1);
  background: none;
  color: var(--text-light, #ecf0f1);
  cursor: pointer;
}

.deck-panel-actions {
  flex-shrink: 0;
}

.deck-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: auto;
  padding-top: 15px;
  flex-shrink: 0;
}

.action-button {
  padding: 10px;
  border: none;
  border-radius: 5px;
  color: white;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.import-button {
  background-color: #3498db;
}

.import-button:hover {
  background-color: #5dade2;
}

.export-button {
  background-color: #27ae60;
}

.export-button:hover {
  background-color: #2ecc71;
}

.edit-text-button {
  background-color: #8e44ad;
  grid-column: 1 / -1;
}

.edit-text-button:hover {
  background-color: #9b59b6;
}

.clear-button {
  background-color: #f39c12;
  grid-column: 1 / -1;
}

.clear-button:hover {
  background-color: #f5b041;
}

.start-game-button {
  width: 100%;
  padding: 15px;
  font-size: 1.2em;
  background-color: #e63946;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.start-game-button:hover {
  background-color: #f2525e;
}

.start-game-button:disabled {
  background-color: #7f8c8d;
  cursor: not-allowed;
}

.start-game-button:disabled:hover {
  background-color: #7f8c8d;
}

/* テキストエディタのモーダル用スタイル */
.text-editor-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  /* 他のUIより手前に表示 */
}

.text-editor-content {
  background-color: var(--board-bg, #34495e);
  padding: 20px 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  border: 1px solid var(--area-border, #7f8c8d);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
}

.text-editor-content h3 {
  margin: 0;
  color: var(--text-light, #ecf0f1);
  border-bottom: 1px solid var(--area-border, #7f8c8d);
  padding-bottom: 10px;
}

.text-editor-content p {
  margin: 0;
  font-size: 0.9em;
  color: #bdc3c7;
}

.deck-textarea {
  width: 100%;
  height: 45vh;
  /* ビューポートの高さの45% */
  background-color: #2c3e50;
  color: var(--text-light, #ecf0f1);
  border: 1px solid var(--area-border, #7f8c8d);
  border-radius: 4px;
  padding: 10px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  font-size: 1em;
  line-height: 1.5;
  resize: vertical;
}

.text-editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.apply-button {
  background-color: #27ae60;
}

.apply-button:hover {
  background-color: #2ecc71;
}

.cancel-button {
  background-color: #e63946;
}

.cancel-button:hover {
  background-color: #f2525e;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--area-border, #7f8c8d);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #95a5a6;
}
</style>